import { computed, inject, Injectable, signal } from '@angular/core';
import { Decimal } from 'decimal.js';
import onChange from 'on-change';
import { confirm } from 'devextreme/ui/dialog';
import { ToastrService } from 'ngx-toastr';
import { BaseFormService } from 'ag-common-svc/public-api';
import { DashboardReportingApiService } from 'ag-common-svc/lib/services/dashboard-reporting-api.service';
import { ImportFileValidatorService } from 'ag-common-svc/lib/services/import-file-validator.service';
import { PolicyTransaction, PolicyTransactionKeys } from 'ag-common-lib/lib/models/domain/policy-transaction.model';
import { ImportAgentPortalPaidFileResponse } from 'ag-common-lib/lib/contracts/reporting-api/response/dashboard';
import { PaidFileUploadFormData, PaidFileValidationMessage, PaidFileValidationSummary } from './report-administration.models';

/**
 * Owns the paid-file upload modal's form + validation + import. The form data is wrapped in
 * `onChange`, so editing Year / As-of Date (and dropping a file) re-validates automatically —
 * no per-field handlers in the component. The selected File is kept out of the proxy on purpose
 * (papaparse reads it through FileReader, which breaks on a Proxy-wrapped native File).
 */
@Injectable()
export class ReportAdministrationFormService extends BaseFormService<PaidFileUploadFormData> {
  private readonly toastr = inject(ToastrService);
  private readonly dashboardReportingApiService = inject(DashboardReportingApiService);
  private readonly importFileValidatorService = inject(ImportFileValidatorService);

  private readonly _file = signal<File | null>(null);
  private readonly _validationMessages = signal<PaidFileValidationMessage[]>([]);
  private readonly _validationSummary = signal<PaidFileValidationSummary | null>(null);
  private readonly _validationComplete = signal(false);
  private readonly _validationInProgress = signal(false);
  private readonly _importInProgress = signal(false);

  readonly file = this._file.asReadonly();
  readonly validationMessages = this._validationMessages.asReadonly();
  readonly validationSummary = this._validationSummary.asReadonly();
  readonly validationComplete = this._validationComplete.asReadonly();
  readonly isBusy = computed(() => this._validationInProgress() || this._importInProgress());
  readonly isImporting = this._importInProgress.asReadonly();
  // Validation runs automatically, so the only manual action is Import — enabled once a run passes.
  readonly canImport = computed(() => this._validationComplete() && !this.isBusy());

  constructor() {
    super();
    this.initForm();
  }

  /** Reset the modal to a clean state and rebuild the change-tracked form data. */
  initForm(): void {
    this.importFileValidatorService.reset();
    this._file.set(null);
    this._importInProgress.set(false);
    this.resetValidation();
    this.formChangesDetector.clear();
    this.formDataSig.set(this.buildFormData(this.getDefaultFormData()));
  }

  setFile(file: File | null): void {
    const previousFile = this._file();
    this._file.set(file ?? null);
    this.formChangesDetector.handleChange('file', file ?? null, previousFile);
    this.revalidate();
  }

  clearFile(): void {
    const previousFile = this._file();
    this._file.set(null);
    this.formChangesDetector.handleChange('file', null, previousFile);
    this.resetValidation();
  }

  /** Modal primary action. Validation is automatic, so this only runs the import (button is
   *  disabled via canImport until a validation run has passed). */
  async submit(): Promise<void> {
    await this.importFile();
  }

  /** Close guard: block while busy, and confirm a discard if the user has touched the form/file. */
  allowClose = async (): Promise<boolean> => {
    if (this.isBusy()) {
      return false;
    }

    if (!this.formChangesDetector.hasChanges) {
      return true;
    }

    return confirm('<i>You have an unsaved paid-file upload. Close without importing?</i>', 'Discard upload');
  };

  private buildFormData(initial: PaidFileUploadFormData): PaidFileUploadFormData {
    return onChange(initial, (path, value, previousValue) => {
      if (value === previousValue) {
        return;
      }

      this.formChangesDetector.handleChange(String(path), value, previousValue);
      this.formDataSig.update(data => data);
      this.revalidate();
    });
  }

  private revalidate(): void {
    this.resetValidation();

    if (this._file() && !this._validationInProgress()) {
      void this.validate();
    }
  }

  private async validate(): Promise<void> {
    this.resetValidation();

    const requiredFieldErrors = this.getRequiredFieldErrors();
    if (requiredFieldErrors.length) {
      this.setErrorMessages(requiredFieldErrors);
      return;
    }

    const file = this._file();
    if (!this.isCsvFile(file)) {
      this.setErrorMessages(['Select a CSV file before validating.']);
      return;
    }

    this._validationInProgress.set(true);
    const validatorMessages: string[] = [];
    const { year } = onChange.target(this.formDataSig());

    try {
      this.importFileValidatorService.reset();
      const transactions = await this.importFileValidatorService.importPaidFiles([file], validatorMessages, year as number);

      this._validationMessages.set(this.toValidationMessages(this.normalizeMessages(validatorMessages), 'info'));
      this._validationSummary.set(this.buildValidationSummary(transactions));
      this._validationComplete.set(true);
    } catch (error) {
      const normalizedMessages = this.normalizeMessages(validatorMessages);
      const messageTexts = [...normalizedMessages];

      this.getValidationErrorMessages(error, normalizedMessages).forEach(errorMessage => {
        if (!messageTexts.some(message => this.toUserFriendlyErrorMessage(message) === errorMessage)) {
          messageTexts.push(errorMessage);
        }
      });

      this._validationMessages.set(this.toValidationMessages(messageTexts, 'info'));
    } finally {
      this.importFileValidatorService.reset();
      this._validationInProgress.set(false);
    }
  }

  private async importFile(): Promise<void> {
    if (!this._validationComplete() || !this._validationSummary() || this._importInProgress()) {
      return;
    }

    const requiredFieldErrors = this.getRequiredFieldErrors();
    if (requiredFieldErrors.length) {
      this.setErrorMessages(requiredFieldErrors);
      this._validationComplete.set(false);
      this._validationSummary.set(null);
      return;
    }

    const file = this._file();
    const { year, asOfDate } = onChange.target(this.formDataSig());
    this._importInProgress.set(true);

    try {
      const response = await this.dashboardReportingApiService.importAgentPortalPaidFile({
        year: year as number,
        asOfDate: this.formatDateOnly(asOfDate),
        file,
      });

      this.onSuccessfulImport(response, file);
    } catch (error) {
      this.setErrorMessages(this.getApiErrorMessages(error));
    } finally {
      this._importInProgress.set(false);
    }
  }

  private onSuccessfulImport(response: ImportAgentPortalPaidFileResponse, file: File | null): void {
    const importedFileName = response?.data?.fileName ?? file?.name ?? 'Paid file';
    this.toastr.success(`${importedFileName} imported successfully.`, null, { disableTimeOut: true });
    // Notifies the component (via BaseFormService.actions$) to close the modal + refresh the grid.
    this.onSuccessfulCreated();
  }

  private resetValidation(): void {
    this._validationComplete.set(false);
    this._validationSummary.set(null);
    this._validationMessages.set([]);
  }

  private getDefaultFormData(): PaidFileUploadFormData {
    return {
      year: new Date().getFullYear(),
      asOfDate: new Date(),
    };
  }

  private getRequiredFieldErrors(): string[] {
    const errors: string[] = [];
    const { year, asOfDate } = onChange.target(this.formDataSig());

    if (!year) {
      errors.push('Year is required.');
    }

    if (!this.isValidDate(asOfDate)) {
      errors.push('"As of" Date is required.');
    }

    if (!this._file()) {
      errors.push('Select a CSV file before validating.');
    }

    return errors;
  }

  private isCsvFile(file: File | null): file is File {
    if (!file) {
      return false;
    }

    return file.name?.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
  }

  private isValidDate(date: Date | null): date is Date {
    return date instanceof Date && !Number.isNaN(date.getTime());
  }

  private formatDateOnly(date: Date | null): string {
    if (!this.isValidDate(date)) {
      return '';
    }

    const normalizedDate = date instanceof Date ? date : new Date(date);
    const year = normalizedDate.getFullYear();
    const month = String(normalizedDate.getMonth() + 1).padStart(2, '0');
    const day = String(normalizedDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private buildValidationSummary(transactions: PolicyTransaction[]): PaidFileValidationSummary {
    const uniqueSummaryKeys = new Set<string>();
    // Accumulate money/counts with Decimal so 56k+ rows don't drift on binary float and the UI
    // totals match the backend's exact numeric SUM (getStatsByYear) after import.
    let premiumTotal = new Decimal(0);
    let policies = new Decimal(0);

    transactions.forEach(transaction => {
      const agentKey = String(
        transaction?.[PolicyTransactionKeys.agentId] ||
          transaction?.[PolicyTransactionKeys.agentName] ||
          transaction?.[PolicyTransactionKeys.agentNumber] ||
          '',
      ).trim();

      if (agentKey) {
        uniqueSummaryKeys.add(agentKey);
      }

      premiumTotal = premiumTotal.plus(this.toDecimal(transaction?.[PolicyTransactionKeys.weightedPrem]));
      policies = policies.plus(this.toDecimal(transaction?.[PolicyTransactionKeys.policies]));
    });

    return {
      transactionsToImport: transactions.length,
      summariesToImport: uniqueSummaryKeys.size,
      premiumTotal: premiumTotal.toNumber(),
      policies: policies.toNumber(),
    };
  }

  private toDecimal(value: unknown): Decimal {
    if (value === null || value === undefined || value === '') {
      return new Decimal(0);
    }
    try {
      const decimalValue = new Decimal(typeof value === 'number' ? value : String(value).trim());
      return decimalValue.isFinite() ? decimalValue : new Decimal(0);
    } catch {
      return new Decimal(0);
    }
  }

  private getValidationErrorMessages(error: unknown, validationMessages: string[]): string[] {
    const fatalMessages = validationMessages.filter(message => message.toLowerCase().includes('fatal error'));

    if (fatalMessages.length) {
      return fatalMessages.map(message => this.toUserFriendlyErrorMessage(message));
    }

    const errorMessage = error instanceof Error && error.message ? error.message : 'Validation failed.';
    return [this.toUserFriendlyErrorMessage(errorMessage)];
  }

  private getApiErrorMessages(error: unknown): string[] {
    const apiError = error as {
      error?: { message?: string; errors?: unknown; details?: unknown; data?: unknown };
      message?: string;
    };
    const errors = apiError?.error?.errors ?? apiError?.error?.details ?? apiError?.error?.data;

    if (Array.isArray(errors)) {
      return errors.map(item => this.toUserFriendlyErrorMessage(this.stringifyError(item)));
    }

    if (errors && typeof errors === 'object') {
      return Object.values(errors as Record<string, unknown>).flatMap(value =>
        Array.isArray(value)
          ? value.map(item => this.toUserFriendlyErrorMessage(this.stringifyError(item)))
          : [this.toUserFriendlyErrorMessage(this.stringifyError(value))],
      );
    }

    const message = apiError?.error?.message ?? apiError?.message ?? 'Import failed.';
    return [this.toUserFriendlyErrorMessage(message)];
  }

  private toUserFriendlyErrorMessage(message: string): string {
    const normalizedMessage = String(message || '').trim();
    const lowerMessage = normalizedMessage.toLowerCase();

    if (lowerMessage.includes('cannot read properties of undefined') && lowerMessage.includes('match')) {
      return 'The file could not be validated. Check that the selected CSV uses the expected paid-file format and try again.';
    }

    return normalizedMessage.replace(/^error:\s*/i, '').replace(/^fatal error:\s*/i, 'Validation error: ');
  }

  private setErrorMessages(messages: string[]): void {
    this._validationMessages.set(
      this.toValidationMessages(
        messages.map(message => this.toUserFriendlyErrorMessage(message)),
        'error',
      ),
    );
  }

  private toValidationMessages(messages: string[], kind: PaidFileValidationMessage['kind']): PaidFileValidationMessage[] {
    return messages.map(message => {
      const messageKind = this.isValidationErrorMessage(message) ? 'error' : kind;

      return {
        kind: messageKind,
        text: messageKind === 'error' ? this.toUserFriendlyErrorMessage(message) : message,
      };
    });
  }

  private isValidationErrorMessage(message: string): boolean {
    const lowerMessage = String(message || '').toLowerCase();

    return (
      lowerMessage.includes('fatal error') ||
      lowerMessage.startsWith('error:') ||
      lowerMessage.includes('cannot read properties of undefined') ||
      lowerMessage.includes('could not be validated') ||
      lowerMessage.includes('validation error')
    );
  }

  private stringifyError(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message);
    }

    return JSON.stringify(error);
  }

  private normalizeMessages(messages: string[]): string[] {
    return messages
      .filter(message => !!message && !message.startsWith('==='))
      .map(message => message.trim())
      .filter(Boolean);
  }
}
